import {
  ILabel,
  INode,
  IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  Visual,
  Constructor,
} from '@ardoq/yfiles';
import { LabelExportSupport, GraphItem } from '@ardoq/graph';
import { createMultiLabelExporter, LabelStyleProperties } from '@ardoq/export';
import { MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT } from '@ardoq/graph-labeling';
import { MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT } from 'yfilesExtensions/styles/consts';
import { ensureHeaderLayout } from '../ensureHeaderLayout';
import { OPEN_GROUP_OTHER_LABEL_KIND } from './groupHeaderLabelKind';
import { CanvasOpenGroupOtherLabelVisual } from '../../labels/CanvasOpenGroupOtherLabelVisual';
import { MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as PADDING } from '../consts';
import { createLabelStyle, getOpacity, Preset } from '../../shared/styleUtils';
import {
  calculateOtherLabelsLayout,
  getGroupHeaders,
  GROUP_OTHER_LABELS_CONFIG,
  otherLabelsTotalHeight,
} from '../../shared/layoutUtils';

class OpenGroupOtherLabelStyle extends LabelStyleBase {
  readonly groupHeaderLabelKind = OPEN_GROUP_OTHER_LABEL_KIND;
  public static getStyle(
    context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties[] | null {
    const groupHeaders = getGroupHeaders(context);
    if (!groupHeaders) return null;

    const node = label.owner as INode;
    const groupHeader = ensureHeaderLayout(node, groupHeaders);
    if (!groupHeader) return null;

    const { isTransparentized } = (node.tag as GraphItem) ?? {
      isTransparentized: false,
    };
    const { wrappedOtherLabels } = groupHeader;

    return wrappedOtherLabels.map(wrappedLabel =>
      createLabelStyle(
        MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
        { kind: 'lines', lines: wrappedLabel },
        { preset: Preset.Other, isTransparentized }
      )
    );
  }

  public static getLayout(context: IRenderContext, label: ILabel): Rect[] {
    const node = label.owner as INode | null;
    if (!node) return [];

    const groupHeaders = getGroupHeaders(context);
    if (!groupHeaders) return [];

    const groupHeader = ensureHeaderLayout(node, groupHeaders);
    if (!groupHeader) return [];

    return calculateOtherLabelsLayout(groupHeader.wrappedOtherLabels, {
      ...GROUP_OTHER_LABELS_CONFIG,
    });
  }

  constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): unknown {
    if (type === LabelExportSupport) {
      const toAbsolute = (ctx: IRenderContext, lbl: ILabel): Rect[] => {
        const node = lbl.owner as INode | null;
        if (!node) return [];

        const groupHeaders = getGroupHeaders(ctx);
        const header = groupHeaders
          ? ensureHeaderLayout(node, groupHeaders)
          : null;
        if (!header) return [];

        const baseAbsX = node.layout.x + PADDING;
        const baseAbsY =
          node.layout.y +
          Math.max(22, header.mainLabelHeight) +
          header.subLabelHeight +
          PADDING;

        const {
          layout: { bounds },
        } = label;

        const relRects = OpenGroupOtherLabelStyle.getLayout(ctx, lbl);
        return relRects.map(
          r => new Rect(baseAbsX + r.x, baseAbsY + r.y, bounds.width, r.height)
        );
      };

      return {
        toJSON: createMultiLabelExporter(
          toAbsolute,
          OpenGroupOtherLabelStyle.getStyle
        ),
      } as LabelExportSupport;
    }
    return super.lookup(label, type);
  }

  protected override createVisual(
    context: IRenderContext,
    label: ILabel
  ): Visual | null {
    const groupHeaders = getGroupHeaders(context);
    if (!groupHeaders) return null;

    const node = label.owner as INode | null;
    if (!node) return null;
    const header = ensureHeaderLayout(node, groupHeaders);
    if (!header) return null;

    const { isTransparentized } = (node.tag as GraphItem) ?? {
      isTransparentized: false,
    };
    const otherLabels = (node.tag as GraphItem)?.getItemLabels()?.otherLabels;
    if (!otherLabels?.length) return null;
    const styles = otherLabels.map(l => l[3]);

    const rects = calculateOtherLabelsLayout(header.wrappedOtherLabels, {
      ...GROUP_OTHER_LABELS_CONFIG,
    });

    const {
      layout: { bounds },
    } = label;
    const container = new Rect(
      bounds.x,
      bounds.y,
      bounds.width,
      otherLabelsTotalHeight(rects)
    );

    return new CanvasOpenGroupOtherLabelVisual(
      container,
      rects,
      header.wrappedOtherLabels,
      MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_FONT,
      LINE_HEIGHT,
      getOpacity(isTransparentized),
      styles
    );
  }

  protected override getPreferredSize(_label: ILabel): Size {
    return Size.ZERO;
  }
}
export { OpenGroupOtherLabelStyle };
